import { Box, Checkbox, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography, useTheme } from "@mui/material";
import { DragDropContext, Droppable, Draggable, DropResult } from "react-beautiful-dnd";
import { FC } from "react";
import { useAppRouter } from "~/hooks/use-app-router";
import { GetProcessesResults } from "~/queries/useGetProcesses";
import { useBulkUpdateFiles } from "~/queries/useBulkUpdateFiles";
import { useMultiSelectButton } from "~/hooks/use-multi-select-button";
import { ProcessTableHeader } from "./process-table-header";
import { Process } from "~/types/__generated/gql/graphql";
import { MultiSelectMode } from "~/contexts/multi-select-button/multi-select-button-context";
import { $dayjs, DATEYEARMONTH_FORMAT } from "~/libs/dayjs";
interface ProcessCardProps {
  processes: GetProcessesResults;
}
export const ProcessTable: FC<ProcessCardProps> = ({
  processes
}) => {
  const bulkUpdateFiles = useBulkUpdateFiles();
  const maxFields = Math.max(...processes.map(item => item._files.length));
  const {
    goToFileDetailsQuery
  } = useAppRouter();
  const theme = useTheme();
  const borderColor = theme.palette.divider;
  const multiSelectButton = useMultiSelectButton();
  const handleDragEndFile = async (result: DropResult): Promise<void> => {
    const {
      source,
      destination,
      draggableId
    } = result;
    // Dropped outside the column
    if (!destination) {
      console.log("no destination");
      return;
    }
    const currentFileId = draggableId.split("-")[0];
    const sourceProcessId = source.droppableId.split("-")[0];
    const destProcessId = destination.droppableId.split("-")[0];

    // File has not been moved
    if (sourceProcessId === destProcessId && source.index === destination.index) {
      return;
    }
    if (sourceProcessId === destProcessId) {
      // Same process, different fileing index
      const fileProcess = processes.find(process => process._id === destProcessId);
      if (!fileProcess) return;
      const newFiles = Array.from(fileProcess._files);
      const [removed] = newFiles.splice(source.index, 1);
      newFiles.splice(destination.index, 0, removed);
      await bulkUpdateFiles.mutateAsync({
        inputs: newFiles.map((item, index) => ({
          id: item._id,
          input: {
            sortingIndex: index
          }
        }))
      });
    } else {
      // Different process, different fileing index
      const movedFile = processes.map(process => process._files).flat().find(file => file._id === currentFileId);
      if (!movedFile) return;
      const destinationProcess = processes.find(process => process._id === destProcessId);
      if (!destinationProcess) return;
      const newDestinationFiles = Array.from(destinationProcess._files);
      newDestinationFiles.splice(destination.index, 0, movedFile);
      await bulkUpdateFiles.mutateAsync({
        inputs: newDestinationFiles.map((item, index) => ({
          id: item._id,
          input: {
            sortingIndex: index,
            process: item._id === currentFileId ? destProcessId : undefined
          }
        }))
      });
    }
  };
  return <TableContainer sx={{
    borderRadius: 0.5,
    overflow: "auto",
    height: "85vh"
  }} component={Paper} data-sentry-element="TableContainer" data-sentry-component="ProcessTable" data-sentry-source-file="process-table.tsx">
			<Table sx={{
      ".MuiTableCell-root": {
        minWidth: "280px"
      }
    }} stickyHeader data-sentry-element="Table" data-sentry-source-file="process-table.tsx">
				<TableHead data-sentry-element="TableHead" data-sentry-source-file="process-table.tsx">
					<TableRow key="header-row" data-sentry-element="TableRow" data-sentry-source-file="process-table.tsx">
						{processes.map((p, i) => <ProcessTableHeader key={p._id} process={p as Process} isLast={i + 1 === processes.length} />)}
					</TableRow>
				</TableHead>

				<DragDropContext onDragEnd={handleDragEndFile} data-sentry-element="DragDropContext" data-sentry-source-file="process-table.tsx">
					{/* // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      @ts-ignore */}
					<TableBody data-sentry-element="TableBody" data-sentry-source-file="process-table.tsx">
						{Array.from({
            length: maxFields
          }).map((_, rowIndex) => <TableRow key={rowIndex}>
								{processes.map((item, i) => {
              const field = item._files[rowIndex];
              const droppableId = `${item._id}-col-${i}-row-${rowIndex}`;
              return <Droppable droppableId={droppableId} key={droppableId} index={i}>
											{/* @ts-ignore */}
											{droppableProvided => <TableCell key={item._id} ref={droppableProvided.innerRef} {...droppableProvided.droppableProps} onClick={() => {
                  if (multiSelectButton.isOpen) {
                    if (multiSelectButton.mode === MultiSelectMode.FinanceReport && field.counterConfigs?.monthlyStatement?.index) return;
                    multiSelectButton.handleSelectFile(field._id);
                  } else {
                    void goToFileDetailsQuery(field._id);
                  }
                }} align="left" sx={i + 1 === processes.length ? {} : {
                  borderRight: `1px solid ${borderColor}`
                }}>
													{droppableProvided.placeholder}
													<Draggable key={`${field ? field._id : i}-${droppableId}`} draggableId={`${field ? field._id : i}-${droppableId}`} index={rowIndex}>
														{/* // eslint-disable-next-line @typescript-eslint/ban-ts-comment
               @ts-ignore */}
														{(draggableProvided, _snapshot) => <div ref={draggableProvided.innerRef} {...draggableProvided.draggableProps} {...draggableProvided.dragHandleProps}>
																{field ? <Box sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "flex-start"
                      }}>
																		<Box sx={{
                          flex: 1,
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis"
                        }}>
																			<Typography sx={{
                            fontSize: "0.8rem"
                          }}>
																				{field.counterConfigs?.file?._counter.isPredefinedFormat ? `${$dayjs(field.createdAt).format(DATEYEARMONTH_FORMAT)}` : field.counterConfigs?.file?._counter.prefix}
																				{"-"}
																				{field.counterConfigs?.file?.index ? String(field.counterConfigs.file?.index || 0).padStart(field.counterConfigs.file?._counter?.suffix_digit || 0, "0") : ""}
																				{field.fileSuffixComment ? `-${field.fileSuffixComment}` : ""}
																				<br />
																				{field._client?.name}
																				<br />
																				{field._agent?.name}
																				<br />
																				{field._assignees?.[0]?.firstName}{" "}
																				{field._assignees?.[0]?.lastName}
																			</Typography>
																		</Box>
																		{multiSelectButton.isOpen && <Checkbox disabled={multiSelectButton.mode === MultiSelectMode.FinanceReport && !!field.counterConfigs?.monthlyStatement?.index} checked={multiSelectButton.mode === MultiSelectMode.FinanceReport && field.counterConfigs?.monthlyStatement?.index || (multiSelectButton.selectedFiles ?? []).find(f => f === field._id) ? true : false} />}
																	</Box> : null}
															</div>}
													</Draggable>
												</TableCell>}
										</Droppable>;
            })}
							</TableRow>)}
					</TableBody>
				</DragDropContext>
			</Table>
		</TableContainer>;
};